import React, { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Phone, Mail, Trash2, Eye, X, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sg_admin_token";

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t || token}` } });
      setLeads(res.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchLeads(); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      fetchLeads(res.data.token);
    } catch (err) { setError("Неверный пароль"); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setLeads([]); };

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    setLeads(leads.map(l => l.id === id ? { ...l, status } : l));
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Удалить заявку?")) return;
    await axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setLeads(leads.filter(l => l.id !== id));
  };

  const statusColors = {
    new: { bg: '#FAF8F5', color: '#1B2A4A', border: '#C9A96E' },
    contacted: { bg: '#FEF3C7', color: '#854D0E', border: '#FCD34D' },
    qualified: { bg: '#DBEAFE', color: '#1E3A8A', border: '#60A5FA' },
    closed: { bg: '#D1FAE5', color: '#065F46', border: '#34D399' }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F5' }}>
        <div className="max-w-sm w-full rounded-2xl p-8" style={{ background: '#fff', border: '1px solid #E8E0D5', boxShadow: '0 20px 60px -30px rgba(27,42,74,0.18)' }}>
          <div className="font-script text-4xl text-center mb-2" style={{ color: '#1B2A4A' }}>studygri</div>
          <div className="text-center text-sm mb-7" style={{ color: '#666' }}>Админ-панель</div>
          <form onSubmit={login} className="space-y-3">
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Пароль" className="h-12" autoFocus />
            {error && <div className="text-xs" style={{ color: '#DC2626' }}>{error}</div>}
            <Button type="submit" className="w-full h-12 rounded-full font-semibold text-white" style={{ background: '#1B2A4A' }}>Войти</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }}>
      <div className="sticky top-0 z-10 bg-white border-b" style={{ borderColor: '#E8E0D5' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-script text-3xl" style={{ color: '#1B2A4A' }}>studygri</span>
            <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: '#C9A96E' }}>Admin</span>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-sm font-medium" style={{ color: '#1B2A4A' }}>
            <LogOut className="w-4 h-4" /> Выйти
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl" style={{ color: '#1B2A4A' }}>Заявки</h1>
            <p className="text-sm mt-1" style={{ color: '#666' }}>{leads.length} всего · {leads.filter(l => l.status === 'new').length} новых</p>
          </div>
          <button onClick={() => fetchLeads()} className="text-sm font-medium" style={{ color: '#C9A96E' }}>{loading ? 'Загрузка...' : 'Обновить'}</button>
        </div>

        <div className="rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid #E8E0D5' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#FAF8F5', borderBottom: '1px solid #E8E0D5' }}>
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Дата</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Имя</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Контакты</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Страна</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Программа</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Источник / UTM</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Язык</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>Статус</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-sm" style={{ color: '#666' }}>Пока заявок нет</td></tr>
                )}
                {leads.map(l => {
                  const sc = statusColors[l.status] || statusColors.new;
                  return (
                    <tr key={l.id} onClick={() => setSelectedLead(l)} className="border-t hover:bg-[#FAF8F5] transition cursor-pointer" style={{ borderColor: '#F0EAE0' }} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#666' }}>{new Date(l.created_at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: '#1B2A4A' }}>
                        {l.name}
                        {l.message && <div className="text-xs font-normal mt-1" style={{ color: '#666' }}>{l.message.slice(0, 80)}{l.message.length > 80 ? '…' : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:underline" style={{ color: '#1B2A4A' }}><Phone className="w-3 h-3" /> {l.phone}</a>
                        {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 mt-1 hover:underline" style={{ color: '#666' }}><Mail className="w-3 h-3" /> {l.email}</a>}
                      </td>
                      <td className="px-4 py-3 uppercase text-xs font-semibold" style={{ color: '#1B2A4A' }}>{l.country || '—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#1B2A4A' }}>{l.program || '—'}</td>
                      <td className="px-4 py-3 text-xs align-top" style={{ color: '#1B2A4A' }}>
                        <div className="font-semibold">{l.source || '—'}</div>
                        {(l.utm_source || l.utm_medium || l.utm_campaign || l.utm_content || l.utm_term) && (
                          <div className="mt-1 space-y-0.5" style={{ color: '#666' }}>
                            {l.utm_source && <div><span className="font-mono">src:</span> {l.utm_source}</div>}
                            {l.utm_medium && <div><span className="font-mono">med:</span> {l.utm_medium}</div>}
                            {l.utm_campaign && <div><span className="font-mono">camp:</span> {l.utm_campaign}</div>}
                            {l.utm_content && <div><span className="font-mono">cnt:</span> {l.utm_content}</div>}
                            {l.utm_term && <div><span className="font-mono">trm:</span> {l.utm_term}</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs uppercase" style={{ color: '#666' }}>{l.lang}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger className="h-8 text-xs font-bold uppercase tracking-wider w-32" style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Новый</SelectItem>
                            <SelectItem value="contacted">Связались</SelectItem>
                            <SelectItem value="qualified">Квалифицирован</SelectItem>
                            <SelectItem value="closed">Закрыт</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setSelectedLead(l)} className="text-xs hover:opacity-80" style={{ color: '#1B2A4A' }} title="Просмотр" data-testid={`view-lead-${l.id}`}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => deleteLead(l.id)} className="text-xs hover:opacity-80" style={{ color: '#DC2626' }} title="Удалить"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <LeadDetailsDialog lead={selectedLead} onClose={() => setSelectedLead(null)} />
    </div>
  );
};

const LeadDetailsDialog = ({ lead, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  if (!lead) return null;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(lead.message || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };

  const rows = [
    { k: "Дата", v: new Date(lead.created_at).toLocaleString('ru-RU') },
    { k: "Имя", v: lead.name },
    { k: "Телефон", v: lead.phone },
    { k: "Email", v: lead.email },
    { k: "Страна", v: lead.country },
    { k: "Программа", v: lead.program },
    { k: "Язык", v: (lead.lang || '').toUpperCase() },
    { k: "Источник", v: lead.source },
    { k: "UTM source", v: lead.utm_source },
    { k: "UTM medium", v: lead.utm_medium },
    { k: "UTM campaign", v: lead.utm_campaign },
    { k: "UTM content", v: lead.utm_content },
    { k: "UTM term", v: lead.utm_term },
    { k: "Kommo Lead ID", v: lead.kommo_lead_id },
  ].filter(r => r.v);

  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="lead-details-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b" style={{ borderColor: '#E8E0D5' }}>
          <DialogTitle className="font-display text-2xl" style={{ color: '#1B2A4A' }}>
            Заявка · {lead.name}
          </DialogTitle>
          <div className="text-xs mt-1" style={{ color: '#666' }}>{new Date(lead.created_at).toLocaleString('ru-RU')}</div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-5 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {rows.map((r, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#C9A96E' }}>{r.k}</span>
                <span className="text-sm break-words" style={{ color: '#1B2A4A' }}>{String(r.v)}</span>
              </div>
            ))}
          </div>

          {lead.message && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#C9A96E' }}>Полное сообщение / Стратегия</span>
                <button onClick={copyMessage} className="flex items-center gap-1 text-xs font-medium hover:opacity-80" style={{ color: '#1B2A4A' }} data-testid="copy-message-btn">
                  <Copy className="w-3 h-3" /> {copied ? 'Скопировано' : 'Копировать'}
                </button>
              </div>
              <pre className="whitespace-pre-wrap break-words text-sm rounded-xl p-4 font-sans leading-relaxed" style={{ background: '#FAF8F5', border: '1px solid #E8E0D5', color: '#1B2A4A' }} data-testid="lead-full-message">
{lead.message}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPage;